/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {
  assetsUpdatesSelector,
  filteredAssetsSelector,
  filteredDevicesSelector,
  findNearbyAssetsSelector,
  findNearbyIsActiveSelector,
  gridFilterModelSelector,
  isLargeFleetModeSelector,
  scDevicesSelector,
  scDevicesUpdatesSelector,
  trackingAssetsSelector
} from '@/store/selectors/tracking-selectors';
import {filterAssetsByGridModel} from '../../TrackingAssetClientGrid';
import {useFindNearbyFilter} from './useFindNearbyFilter';

/**
 * The useGridFilteredItems hook is used to keep the filtered Items from the Grid,
 * the states store assets and devices data and also their respective updates
 *
 *
 * ```typescript
 *
 *   import {useGridFilteredItems} from './hook/tracking/useGridFilteredItems';
 *
 *   const {gridFilteredAssets, gridFilteredAssetsUpdates, ...} = useGridFilteredItems();
 *
 * ```
 */
export const useGridFilteredItems = (includeSelectedAsset: boolean = false) => {
  // Selectors
  const assets           = useSelector(trackingAssetsSelector);
  const filteredAssets   = useSelector(filteredAssetsSelector);
  const assetsUpdates    = useSelector(assetsUpdatesSelector);
  const scDevices        = useSelector(scDevicesSelector);
  const filteredDevices  = useSelector(filteredDevicesSelector);
  const scDevicesUpdates = useSelector(scDevicesUpdatesSelector);
  const filterModel      = useSelector(gridFilterModelSelector);
  const isLargeFleetMode = useSelector(isLargeFleetModeSelector);

  const findNearbyIsActive            = useSelector(findNearbyIsActiveSelector);
  const nearByAssets                  = useSelector(findNearbyAssetsSelector);
  const removeSelectedAssetFindNearby = useFindNearbyFilter();
  const findNearbyAssets              = !includeSelectedAsset
    ? removeSelectedAssetFindNearby(nearByAssets) : nearByAssets;

  const gridFilteredAssetsUpdates = useMemo(() => (
    filterAssetsByGridModel(assetsUpdates, filterModel)
  ), [assetsUpdates, filterModel]);

  const gridFilteredDevicesUpdates = useMemo(() => (
    filterAssetsByGridModel(scDevicesUpdates as any, filterModel)
  ), [scDevicesUpdates, filterModel]);

  const gridFilteredAssets = useMemo(() => {
    let currentAssets;
    if (findNearbyIsActive) {
      currentAssets = filterAssetsByGridModel(findNearbyAssets, filterModel);
    } else if (!isLargeFleetMode && !isEmpty(filterModel)) {
      currentAssets = filteredAssets;
    } else {
      currentAssets = assets;
    }
    return currentAssets || [];
  }, [
    isLargeFleetMode,
    findNearbyIsActive,
    findNearbyAssets,
    filterModel,
    assets,
    filteredAssets
  ]);

  const gridFilteredDevices = useMemo(() => {
    let currentDevices;
    if (findNearbyIsActive) {
      currentDevices = [];
    } else if (!isLargeFleetMode && !isEmpty(filterModel)) {
      currentDevices = filteredDevices;
    } else {
      currentDevices = scDevices;
    }
    return currentDevices || [];
  }, [
    findNearbyIsActive,
    isLargeFleetMode,
    filterModel,
    filteredDevices,
    scDevices
  ]);

  return {
    gridFilteredAssets,
    gridFilteredDevices,
    gridFilteredAssetsUpdates,
    gridFilteredDevicesUpdates
  };
};

export default useGridFilteredItems;
